import { waitFor } from "@frontiers-vue3/public-pages";
import { MongoClient, ReadPreference, type ConnectOptions } from "mongodb";

type ConnectionStatus =
  | "disconnecting"
  | "disconnected"
  | "connecting"
  | "connected";

function stripConnectionOptions(uri: string): {
  url: string;
  options: ConnectOptions;
} {
  const splitUri = uri.split("?");
  const url = splitUri[0];

  let options: Record<string, unknown> = {};
  splitUri[1]?.split("&").forEach((option) => {
    const splitOption = option.split("=");
    options[splitOption[0]] = splitOption[1];
  });

  return { url, options };
}

export class MongoDbConnection {
  public client!: MongoClient;
  private mongoUri!: string;
  private mongoOptions!: ConnectOptions;
  private connStatus: ConnectionStatus = "disconnected";

  public constructor(
    private mongodbUri: string,
    private mongodbIsDirectConn: boolean
  ) {
    if (!this.mongodbUri)
      throw new Error("MongoDB URI is not defined in runtimeConfig");

    const { url, options } = stripConnectionOptions(this.mongodbUri);
    this.mongoUri = url;
    this.mongoOptions = options;

    this.createClient();
  }

  private createClient(): void {
    if (this.client) return;

    this.client = new MongoClient(this.mongoUri, {
      ...this.mongoOptions,
      directConnection: this.mongodbIsDirectConn,
      readPreference: ReadPreference.SECONDARY,
      appName: "research-topic-2023",
      minPoolSize: 0,
      maxPoolSize: 100,
      maxIdleTimeMS: 5000
    });
  }

  public async connect(): Promise<void> {
    if (this.connStatus === "connected") return;
    if (this.connStatus === "connecting") {
      return await this.waitForConnectionStatus("connected");
    }
    if (this.connStatus === "disconnecting") {
      await this.waitForConnectionStatus("disconnected");
    }

    this.createClient();

    try {
      this.connStatus = "connecting";
      await this.client.connect();
      this.connStatus = "connected";
    } catch (err) {
      this.connStatus = "disconnected";
      throw new Error(`Error connecting to MongoDB: ${err}`);
    }
  }

  public async disconnect(): Promise<void> {
    if (!this.client) {
      return;
    }
    if (this.connStatus === "connecting") {
      await this.waitForConnectionStatus("connected");
    }
    if (this.connStatus !== "connected") {
      return;
    }

    try {
      this.connStatus = "disconnecting";
      await this.client.close();
      console.log("mongodb-connection > disconnected");
    } catch (err) {
      console.error("Error during MongoDB disconnection:", err);
    } finally {
      this.connStatus = "disconnected";
    }
  }

  private waitForConnectionStatus(status: ConnectionStatus): Promise<void> {
    return waitFor(() => this.connStatus === status, 3, 100);
  }
}
